// Combinable audience selector for the banner create/edit dialog.
//
// Two visually separate sections:
//
//   1. Customer audience (top) — all-customers toggle, customer-
//      groups multi-select, specific-customers typeahead. AT LEAST
//      ONE of these must be populated for the banner to have an
//      audience.
//   2. Role filter (bottom) — Admin / Standard user chips that
//      NARROW the audience set. Selecting no roles means "everyone
//      in the audience set, regardless of role". Role selection
//      alone is NOT a valid audience.
//
// The component is intentionally controlled — the parent dialog
// owns the audience state so the publish-confirmation modal can
// read the same dedupe-reach the operator just saw.

import React, { useEffect, useMemo, useState } from 'react'
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { useCustomerGroups } from 'services/queries/use-customer-groups'
import { useCustomerTypeahead } from 'services/queries/use-customer-typeahead'
import { useCustomersByIds } from 'services/queries/use-customers-by-ids'
import {
  BannerAudienceT,
  BannerRoleT,
  CustomerGroupT,
  CustomerTypeaheadResultT,
} from 'types/global'
import { computeDedupedReach, formatReachPhrase, roleLabel } from './helpers'

const TYPEAHEAD_DEBOUNCE_MS = 250

type CustomerOption = CustomerTypeaheadResultT

interface AudienceSelectorProps {
  value: BannerAudienceT
  onChange: (next: BannerAudienceT) => void
  // Hydrated customer chip labels (id -> "local_id — name") so an
  // edit-mode dialog can render chips for previously selected
  // customers picked up from the typeahead cache. When the operator
  // picks a customer in this dialog session we update this map
  // automatically.
  customerLabelById?: Map<number, string>
  onCustomerLabelLearned?: (id: number, label: string) => void
}

const formatCustomerOptionLabel = (c: {
  local_id: string
  name: string
}): string => `${c.local_id} — ${c.name}`

const formatGroupOptionLabel = (g: CustomerGroupT): string =>
  `${g.name} (${g.customer_ids.length})`

const AudienceSelector: React.FC<AudienceSelectorProps> = ({
  value,
  onChange,
  customerLabelById,
  onCustomerLabelLearned,
}) => {
  const { data: groupsResponse } = useCustomerGroups()
  const allGroups: CustomerGroupT[] =
    groupsResponse && groupsResponse.success ? groupsResponse.data : []

  // Hydrate group chips by id. Groups are pre-loaded so we always
  // have a real label — unlike specific customers which need a
  // placeholder on edit mode (see CLAUDE-style spec for ENG2-2007
  // Phase 1: no batch lookup endpoint yet).
  const selectedGroups: CustomerGroupT[] = useMemo(
    () =>
      value.target_group_ids
        .map((id) => allGroups.find((g) => g.id === id))
        .filter((g): g is CustomerGroupT => Boolean(g)),
    [value.target_group_ids, allGroups],
  )

  // Specific customers — typeahead pick state. The chips render
  // from `value.target_customer_ids` directly (placeholder labels
  // are tolerated for edit-mode seeding).
  const [customerSearch, setCustomerSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  useEffect(() => {
    const handle = setTimeout(
      () => setDebouncedSearch(customerSearch),
      TYPEAHEAD_DEBOUNCE_MS,
    )
    return () => clearTimeout(handle)
  }, [customerSearch])

  const { data: typeaheadResponse, isFetching: isTypeaheadLoading } =
    useCustomerTypeahead(debouncedSearch)

  // Operator-friendly chip labels for the specific-customers picker.
  // Seeded from edit mode and topped up as the operator picks new
  // customers via the typeahead. The "?" placeholder for chips
  // hydrated from `customer_ids` alone is rendered in the chip
  // body — see the renderTags block.
  const [learnedLabels, setLearnedLabels] = useState<Map<number, string>>(() =>
    customerLabelById ? new Map(customerLabelById) : new Map(),
  )

  // On edit-mode mount the dialog hands us a `target_customer_ids`
  // list but no chip labels. Resolve them in one batch via the new
  // GET /customer?ids=… endpoint and back-fill `learnedLabels`. The
  // hook is gated on the unresolved-ids array being non-empty so we
  // don't fire when everything is already cached.
  const unresolvedIds = useMemo(
    () =>
      value.target_customer_ids.filter((id) => !learnedLabels.has(id)),
    [value.target_customer_ids, learnedLabels],
  )
  const { data: byIdsResponse } = useCustomersByIds(unresolvedIds)
  useEffect(() => {
    if (!byIdsResponse || !byIdsResponse.success) return
    if (byIdsResponse.data.length === 0) return
    setLearnedLabels((prev) => {
      const next = new Map(prev)
      for (const row of byIdsResponse.data) {
        const label = formatCustomerOptionLabel(row)
        if (next.get(row.id) === label) continue
        next.set(row.id, label)
        if (onCustomerLabelLearned) onCustomerLabelLearned(row.id, label)
      }
      return next
    })
  }, [byIdsResponse, onCustomerLabelLearned])

  const typeaheadOptions = useMemo<CustomerOption[]>(() => {
    if (!typeaheadResponse || !typeaheadResponse.success) return []
    const selected = new Set(value.target_customer_ids)
    return typeaheadResponse.data.filter((row) => !selected.has(row.id))
  }, [typeaheadResponse, value.target_customer_ids])

  const selectedCustomerOptions: CustomerOption[] = useMemo(
    () =>
      value.target_customer_ids.map((id) => {
        const label = learnedLabels.get(id)
        if (label) {
          // Split "local_id — name" back into the typeahead shape.
          const dashIdx = label.indexOf(' — ')
          if (dashIdx >= 0) {
            return {
              id,
              local_id: label.slice(0, dashIdx),
              name: label.slice(dashIdx + 3),
              country: '',
            }
          }
        }
        return {
          id,
          local_id: String(id),
          name: '(existing member)',
          country: '',
        }
      }),
    [value.target_customer_ids, learnedLabels],
  )

  const reach = useMemo(
    () =>
      computeDedupedReach(
        value.target_customer_ids,
        value.target_group_ids,
        allGroups,
      ),
    [value.target_customer_ids, value.target_group_ids, allGroups],
  )

  // Under the new AND-joined semantics the role filter narrows the
  // customer audience rather than supplying its own — picking roles
  // alone is NOT a valid audience. Empty check therefore ignores
  // `target_roles` entirely.
  const audienceEmpty =
    !value.target_all &&
    value.target_group_ids.length === 0 &&
    value.target_customer_ids.length === 0

  const updateRoles = (next: BannerRoleT[]) =>
    onChange({ ...value, target_roles: next })

  const toggleRole = (role: BannerRoleT) => {
    if (value.target_roles.includes(role)) {
      updateRoles(value.target_roles.filter((r) => r !== role))
    } else {
      updateRoles([...value.target_roles, role])
    }
  }

  const handleCustomersChange = (next: CustomerOption[]) => {
    // Learn labels for newly picked rows so chips render nicely on
    // re-render and the parent can persist them for next time.
    const nextLabels = new Map(learnedLabels)
    for (const opt of next) {
      const label = formatCustomerOptionLabel(opt)
      if (label !== `${opt.id} — (existing member)`) {
        nextLabels.set(opt.id, label)
        if (onCustomerLabelLearned) onCustomerLabelLearned(opt.id, label)
      }
    }
    setLearnedLabels(nextLabels)
    onChange({ ...value, target_customer_ids: next.map((o) => o.id) })
  }

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      data-testid="audience-selector"
    >
      {/* ---------------- Customer audience ---------------- */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: 'text.primary',
            textTransform: 'uppercase',
            letterSpacing: 0.4,
          }}
        >
          Customer audience
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={value.target_all}
              onChange={(_e, checked) =>
                onChange({
                  ...value,
                  target_all: checked,
                  // Clear the two customer-axis sub-selectors when
                  // "all" goes on — keeps the wire payload tidy. The
                  // role filter is NOT a customer-axis under the new
                  // AND-joined semantics, so it stays put across the
                  // toggle.
                  target_group_ids: checked ? [] : value.target_group_ids,
                  target_customer_ids: checked ? [] : value.target_customer_ids,
                })
              }
              data-cy="audience-all-toggle"
            />
          }
          label={
            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
              Show to all customers
            </Typography>
          }
        />

        {!value.target_all && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* ---------------- Customer groups ---------------- */}
            <Box>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: 'text.secondary',
                  mb: 0.5,
                }}
              >
                Customer groups
              </Typography>
              <Autocomplete<CustomerGroupT, true, false, false>
                multiple
                options={allGroups}
                value={selectedGroups}
                onChange={(_e, next) =>
                  onChange({
                    ...value,
                    target_group_ids: next.map((g) => g.id),
                  })
                }
                getOptionLabel={formatGroupOptionLabel}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                renderTags={(rows, getTagProps) =>
                  rows.map((row, index) => (
                    <Chip
                      size="small"
                      label={formatGroupOptionLabel(row)}
                      {...getTagProps({ index })}
                      key={row.id}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder={
                      allGroups.length === 0
                        ? 'No customer groups available'
                        : 'Pick one or more groups...'
                    }
                  />
                )}
              />
            </Box>

            {/* ---------------- Specific customers ---------------- */}
            <Box>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: 'text.secondary',
                  mb: 0.5,
                }}
              >
                Specific customers
              </Typography>
              <Autocomplete<CustomerOption, true, false, false>
                multiple
                options={typeaheadOptions}
                value={selectedCustomerOptions}
                onChange={(_e, next) => handleCustomersChange(next)}
                inputValue={customerSearch}
                onInputChange={(_e, next) => setCustomerSearch(next)}
                getOptionLabel={formatCustomerOptionLabel}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                filterOptions={(opts) => opts}
                loading={isTypeaheadLoading}
                noOptionsText={
                  debouncedSearch.trim().length === 0
                    ? 'Start typing to search customers'
                    : 'No matching customers'
                }
                renderTags={(rows, getTagProps) =>
                  rows.map((row, index) => {
                    const isPlaceholder = row.name === '(existing member)'
                    const label = isPlaceholder
                      ? `${row.local_id} — ?`
                      : formatCustomerOptionLabel(row)
                    return (
                      <Chip
                        size="small"
                        label={label}
                        title={
                          isPlaceholder
                            ? 'Existing member — re-pick to load the real name'
                            : undefined
                        }
                        {...getTagProps({ index })}
                        key={row.id}
                      />
                    )
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search by company name or local ID..."
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isTypeaheadLoading ? (
                            <CircularProgress size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* ---------------- Role filter ---------------- */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          pt: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
        data-testid="role-filter"
      >
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: 'text.primary',
            textTransform: 'uppercase',
            letterSpacing: 0.4,
          }}
        >
          Role filter
        </Typography>
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          Optional — narrows the audience above. Leave empty to show the banner
          to everyone in the audience set, regardless of role.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
          {(['admin', 'user'] as BannerRoleT[]).map((role) => {
            const checked = value.target_roles.includes(role)
            return (
              <Chip
                key={role}
                label={roleLabel(role)}
                color={checked ? 'primary' : 'default'}
                variant={checked ? 'filled' : 'outlined'}
                onClick={() => toggleRole(role)}
                data-cy={`role-chip-${role}`}
              />
            )
          })}
        </Box>
      </Box>

      {/* ---------------- Live preview ---------------- */}
      <Box
        sx={{
          mt: 1,
          p: 1.5,
          borderRadius: 1,
          border: '1px solid',
          borderColor: audienceEmpty ? 'error.main' : 'divider',
          bgcolor: audienceEmpty ? 'error.50' : 'action.hover',
        }}
        data-testid="audience-preview"
      >
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5 }}>
          Live audience preview
        </Typography>
        {audienceEmpty ? (
          <Typography
            sx={{ fontSize: 14, fontWeight: 500, color: 'error.main' }}
            data-testid="audience-empty-warning"
          >
            No customer audience selected — pick all customers, a customer
            group, or a specific customer. Roles alone do not constitute an
            audience.
          </Typography>
        ) : value.target_all ? (
          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
            {formatReachPhrase(reach, value.target_roles, true)}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
            {[
              ...selectedGroups.map(
                (g) => `${g.name} (${g.customer_ids.length})`,
              ),
              ...value.target_customer_ids.map((id) => {
                const label = learnedLabels.get(id)
                return label ? label.split(' — ')[0] : String(id)
              }),
            ].join(' + ')}{' '}
            ·{' '}
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {formatReachPhrase(reach, value.target_roles, false)}
            </Box>
          </Typography>
        )}
      </Box>
    </Box>
  )
}

/**
 * Convenience hook: returns `{ reach, isEmpty }` for a given
 * audience value. Used by the parent dialog to drive the publish-
 * confirmation modal copy without re-computing the dedupe.
 */
export function useAudienceSummary(value: BannerAudienceT) {
  const { data: groupsResponse } = useCustomerGroups()
  const allGroups: CustomerGroupT[] =
    groupsResponse && groupsResponse.success ? groupsResponse.data : []

  const reach = useMemo(
    () =>
      computeDedupedReach(
        value.target_customer_ids,
        value.target_group_ids,
        allGroups,
      ),
    [value.target_customer_ids, value.target_group_ids, allGroups],
  )

  // Mirrors the AudienceSelector's `audienceEmpty` check — under the
  // new AND-joined semantics the role filter narrows the customer
  // audience rather than supplying its own, so `target_roles` is
  // explicitly NOT consulted here.
  const isEmpty =
    !value.target_all &&
    value.target_group_ids.length === 0 &&
    value.target_customer_ids.length === 0

  return {
    reach,
    isEmpty,
    hasRoles: value.target_roles.length > 0,
    roles: value.target_roles,
  }
}

export default AudienceSelector
